import { useEffect, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useListingStatusContext } from 'src/contexts/ListingStatusContext';
import { SimpleTable } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { PosManagedMarketplace } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { compareMarketplace } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ListingClient,
  ListingDetails,
  ListingMetrics,
  ListingStatus,
  ListingStatusInfo,
  Marketplace,
} from 'src/WebApiController';

import { useMarketplaceBroadcastSection } from './MarketplaceBroadcastSection.hooks';
import {
  hasPendingMarketplaces,
  updateMarketplaceStatusesAndListing,
} from './MarketplaceBroadcastSection.utils';
import { MarketplaceDisplay } from './MarketplaceDisplay';
import * as styles from './MarketplaceDisplay.css';

export const MarketplaceTable = ({
  listing,
  disabled,
  marketplaceListingStatus,
  setMarketplaceListingStatus,
}: {
  listing: ListingDetails;
  disabled?: boolean;
  marketplaceListingStatus: Record<Marketplace, ListingStatus>;
  setMarketplaceListingStatus: React.Dispatch<
    React.SetStateAction<Record<Marketplace, ListingStatus>>
  >;
}) => {
  const hasReducedBroadcastStatusPullingFrequencyFeature = useUserHasFeature(
    Feature.ReducedBroadcastStatusPullingFrequency
  );
  const { setActivePosEntity, updateActivePosEntityInfo } =
    useActivePosEntityContext<ListingDetails>();

  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const [isLoading, setIsLoading] = useState(false);

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const { mkpListings } = listing;

  const { getMarketplaceDisplayProps, event, isInternationalEvent } =
    useMarketplaceBroadcastSection(
      listing,
      disabled ?? false,
      setIsLoading,
      marketplaceListingStatus,
      setMarketplaceListingStatus
    );

  const hasBatchGetListingStatusCallsFeature = useUserHasFeature(
    Feature.BatchGetListingStatusCalls
  );

  const { getOrSubscribeForListingStatus, isLoading: statusIsLoading } =
    useListingStatusContext();

  useEffect(() => {
    const pollingFrequency = hasBatchGetListingStatusCallsFeature
      ? 2_000
      : hasReducedBroadcastStatusPullingFrequencyFeature
        ? 6_000
        : 2_000;
    // If we have any Marketplace listing pending status - we need to poll to get the latest status
    const statusChecking = setInterval(async () => {
      const shouldCheckStatus =
        hasPendingMarketplaces(marketplaceListingStatus) &&
        !isLoading &&
        !statusIsLoading;

      if (shouldCheckStatus) {
        await tryInvokeApi(
          async () => {
            setIsLoading(true);

            let newListing: ListingStatusInfo | undefined = undefined;
            if (hasBatchGetListingStatusCallsFeature) {
              // When we have batch call feature, we should just skip the per-listing polling
              newListing = getOrSubscribeForListingStatus(listing.id);
            } else {
              const client = new ListingClient(activeAccountWebClientConfig);
              newListing = await client.getListingStatusByListingId(listing.id);
            }

            if (newListing) {
              updateMarketplaceStatusesAndListing(
                statusChecking,
                listing,
                newListing,
                marketplaceListingStatus,
                setMarketplaceListingStatus,
                (updatedListing) => {
                  refreshMetrics?.();
                  updateActivePosEntityInfo({
                    event: event,
                    posEntity: updatedListing,
                    posEntityId: newListing.id,
                    posEntityDisplayId: newListing.idOnMkp,
                  });
                }
              );
            }
          },
          (error) => {
            // Because this is a background process - we don't want to show error dialog
            trackError('ListingClient.getListingStatusByListingId', error, {
              listingId: listing.id,
            });
          },
          () => setIsLoading(false)
        );
      }
    }, pollingFrequency);

    return () => clearInterval(statusChecking);
  }, [
    activeAccountWebClientConfig,
    event,
    listing,
    listing.id,
    listing.idOnMkp,
    marketplaceListingStatus,
    mkpListings,
    refreshMetrics,
    setActivePosEntity,
    updateActivePosEntityInfo,
    trackError,
    isLoading,
    setMarketplaceListingStatus,
    hasReducedBroadcastStatusPullingFrequencyFeature,
    hasBatchGetListingStatusCallsFeature,
    getOrSubscribeForListingStatus,
    statusIsLoading,
  ]);

  return (
    <SimpleTable.Table>
      <SimpleTable.Thead>
        <SimpleTable.Tr>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Merchant} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Broadcast} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Status} />
          </SimpleTable.Th>

          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Section} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Row} />
          </SimpleTable.Th>
          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.Seats} />
          </SimpleTable.Th>

          <SimpleTable.Th
            className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
          >
            <Content id={ContentId.ListingId} />
          </SimpleTable.Th>
        </SimpleTable.Tr>
      </SimpleTable.Thead>
      <SimpleTable.Tbody>
        {mkpListings
          ?.filter((ml) => ml.mkp && !PosManagedMarketplace.includes(ml.mkp))
          .toSorted((a, b) => compareMarketplace(a.mkp, b.mkp))
          .flatMap((ml, i) => {
            if (ml.mkp === Marketplace.StubHub) {
              return [
                <MarketplaceDisplay
                  {...getMarketplaceDisplayProps(ml, false, i)}
                  key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                />,
                <MarketplaceDisplay
                  {...getMarketplaceDisplayProps(ml, true, i)}
                  key={`${ml.mkpListingId}-${ml.mkp}-${true}`}
                />,
              ];
            }

            return !isInternationalEvent
              ? [
                  // We only show other marketplaces when the Event is US
                  <MarketplaceDisplay
                    {...getMarketplaceDisplayProps(ml, false, i)}
                    key={`${ml.mkpListingId}-${ml.mkp}-${false}`}
                  />,
                ]
              : [];
          })}
      </SimpleTable.Tbody>
    </SimpleTable.Table>
  );
};
